import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type FocusSession,
  type FocusSessionDto,
  focusKeys,
  focusMeQueryOptions,
  toFocusSession,
} from '@/entities/focus-session'
import {
  type PomodoroSessionDto,
  type PomodoroStateView,
  pomodoroKeys,
  toPomodoroSession,
} from '@/entities/pomodoro'
import { ApiError, apiErrorText, apiPost, unwrap } from '@/shared/api'
import { toast } from '@/shared/ui/toast'

// Фокус taskless: вход не про задачу, только «я сосредоточен» (+ опц. помодоро).
export interface EnterFocusVars {
  withPomodoro: boolean
}

export interface ExitFocusVars {
  sessionId: string
  pomodoroSessionId?: string
}

function setPomodoroSession(qc: QueryClient, dto: PomodoroSessionDto | null): void {
  qc.setQueryData<PomodoroStateView>(pomodoroKeys.state(), (prev) =>
    prev ? { ...prev, session: dto ? toPomodoroSession(dto) : null } : prev,
  )
}

// Войти в фокус: POST /focus/start (+ опц. /pomodoro/start), обе ручки без задачи.
// Оркестрация независимых операций — роль фронта (серверного оркестратора нет).
export function useEnterFocus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ withPomodoro }: EnterFocusVars): Promise<FocusSession> => {
      let session: FocusSession
      try {
        session = toFocusSession(unwrap<FocusSessionDto>(await apiPost('/focus/start')))
      } catch (err) {
        // Вторая сессия — не ошибка: подхватываем уже идущую через /me.
        if (err instanceof ApiError && err.code === 'FOCUS_ALREADY_RUNNING') {
          const existing = await qc.fetchQuery(focusMeQueryOptions)
          if (!existing) throw err
          session = existing
        } else {
          throw err
        }
      }
      if (withPomodoro) {
        try {
          setPomodoroSession(qc, unwrap<PomodoroSessionDto>(await apiPost('/pomodoro/start', {})))
        } catch (err) {
          // Уже идущее помодоро — тоже не ошибка входа.
          if (!(err instanceof ApiError && err.code === 'TIME_POMODORO_RUNNING')) throw err
        }
      }
      return session
    },
    onSuccess: (session) => {
      qc.setQueryData(focusKeys.me(), session)
      void qc.invalidateQueries({ queryKey: focusKeys.active() })
    },
    onError: (err) => toast.error(apiErrorText(err, {}, 'Не удалось войти в фокус')),
  })
}

// Выйти из фокуса: POST /focus/stop (+ опц. остановить помодоро). Трекинг времени
// независим — время задачи в работе продолжает идти.
export function useExitFocus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId, pomodoroSessionId }: ExitFocusVars): Promise<void> => {
      await apiPost('/focus/stop', { session_id: sessionId })
      if (pomodoroSessionId) {
        try {
          await apiPost('/pomodoro/stop', { session_id: pomodoroSessionId })
        } catch (err) {
          if (!(err instanceof ApiError && err.code === 'TIME_POMODORO_STOPPED')) throw err
        }
      }
    },
    onSuccess: (_data, vars) => {
      qc.setQueryData(focusKeys.me(), null)
      void qc.invalidateQueries({ queryKey: focusKeys.active() })
      if (vars.pomodoroSessionId) setPomodoroSession(qc, null)
    },
    onError: (err) => toast.error(apiErrorText(err, {}, 'Не удалось выйти из фокуса')),
  })
}
